<template>
  <div
    :ref="id"
    :class="[
      'generic-dropdown-container generic-form__component generic-select',
      computedClasses,
    ]"
  >
    <legend
      v-if="inputLabel"
      :id="`generic-select__label--${id}`"
      :class="`generic-select__label generic-select__label--${id}`"
      :for="`select-${id}`"
    >
      <span
        >{{ inputLabel
        }}<span
          v-if="inputLabel && required"
          class="required"
          aria-hidden="true"
          >*</span
        ></span
      >
    </legend>

    <div class="generic-select__dropdown">
      <div v-if="readonly" class="read-only">{{ defaultValue }}</div>

      <template v-else>
        <button
          :id="`select-${id}`"
          ref="combobox"
          role="combobox"
          type="input"
          class="generic-select__combobox"
          :aria-controls="`generic-select__listbox--${id}`"
          :aria-expanded="expanded"
          aria-haspopup="listbox"
          :aria-describedby="descriptionId"
          :aria-label="inputLabel"
          :aria-activedescendant="activeDescendant"
          :aria-invalid="invalid"
          :aria-required="required"
          :required="required"
          :disabled="disabled"
          @focus="handleFocus($event, true)"
          @blur="handleFocus($event, false)"
          @click.prevent.self="handleExpand($event, true)"
          @keydown.space.prevent="handleExpand($event, true)"
          @keydown.enter.prevent="handleExpand($event, true)"
          @keydown.esc.prevent="handleExpand($event, false)"
          @keydown.down="navigateList"
          @keydown.up="navigateList"
          v-htmlsafe="getSelectedOption"
        ></button>

        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
        <div
          :id="`generic-select__listbox--${id}`"
          class="generic-select__listbox"
          role="listbox"
          :aria-labelledby="`generic-select__label--${id}`"
          tabindex="-1"
          @click.stop.prevent="handleOnChange($event, false)"
        >
          <div class="generic-select__listbox-wrapper">
            <slot></slot>
          </div>
        </div>
      </template>
    </div>

    <input type="hidden" :name="inputName" :value="selectedOptions" />
  </div>
</template>

<script>
import { eventBus } from 'EventBus/';
// const combobox = ref(null);

// const expanded = ref(false);
// const activeDescendant = ref(undefined);
// const focus = ref(false);
// const hadFocus = ref(false);
export default {
  name: 'GenericDropdownContainer',
  props: {
    id: {
      type: String,
      default: null,
    },
    inputName: {
      type: String,
      default: null,
    },
    inputLabel: {
      type: String,
      default: '',
    },
    defaultValue: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: true,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    descriptionId: {
      type: [String, Object],
      default: null,
    },
    customClass: {
      type: String,
      default: '',
    },
    hasErrors: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    selectedOptions: {
      type: Array,
      default: () => [],
    },
    multiSelect: {
      type: Boolean,
      default: false,
    },
    customSelectedLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      focus: false,
      hadFocus: false,
      expanded: false,
      combobox: null,
      activeDescendant: undefined,
    };
  },
  computed: {
    computedClasses() {
      return {
        'generic--active': this.focus,
        'generic--disabled': this.disabled,
        'generic--readonly': this.readonly,
        'generic--has-errors': this.invalid || this.hasErrors,
        'generic--raised': this.placeholder || this.hasSelectedOption,
        'generic-select--not-selected': !this.hasSelectedOption,
      };
    },
    hasSelectedOption() {
      return this.selectedOptions.length
        ? this.selectedOptions.some(
            (option) => option !== null && option !== ''
          )
        : false;
    },
    getSelectedOption() {
      return this.hasSelectedOption && !this.expanded
        ? this.customSelectedLabel
          ? this.customSelectedLabel
          : this.selectedOptions.sort().join(', ')
        : this.hasSelectedOption && this.expanded
        ? ''
        : this.placeholder
        ? `<span class="generic-select__placeholder">${this.placeholder}</span>`
        : '';
    },
  },
  mounted() {
    this.combobox = this.$refs.combobox;
    this.setGridStyle();
  },
  methods: {
    setGridStyle() {
      if (!this.combobox) return '';
      setTimeout(() => {
        const hasMultipleChildren = this.combobox.children.length > 1;
        if (hasMultipleChildren) {
          this.combobox.style.gridTemplateColumns = '1.625rem auto';
        } else {
          this.combobox.style.gridTemplateColumns = 'auto';
        }
      }, 10);
      setTimeout(() => {
        Object.values(this.combobox.children).forEach(
          (child) => (child.style.opacity = '1')
        );
      }, 200);
    },
    async handleExpand(event, expand) {
      const redundantBlur =
        !expand &&
        document.activeElement === event.target &&
        event.key !== 'Escape';
      if (redundantBlur) return;

      const isKeyboardSelectAction =
        this.expanded &&
        event instanceof KeyboardEvent &&
        event.key !== 'Escape';
      // eslint-disable-next-line no-unused-vars
      // const isKeyboardExpandAction = !this.expanded && event instanceof KeyboardEvent;
      const { listBoxWrapper, firstOption, activeOption, focusedOption } =
        this.getOptions(event);

      if (isKeyboardSelectAction) {
        this.handleOnChange(event, true);
        if (!this.multiSelect) {
          this.expanded = false;
          focusedOption?.classList.remove('-focused');
        }
      } else {
        this.expanded = expand;
        if (expand) {
          event.target.focus();
          await this.focusSelectedOption(event);
          listBoxWrapper.scrollTop = activeOption
            ? activeOption.offsetTop - 15
            : firstOption.offsetTop - 15;
        } else {
          focusedOption?.classList.remove('-focused');
          setTimeout(() => {
            Object.values(this.combobox.children).forEach(
              (child) => (child.style.opacity = '1')
            );
          }, 10);
          eventBus.$off('form:clicked');
        }
      }
    },
    handleFocus(event, focused) {
      this.hadFocus = true;
      let parent = event.target.parentElement;
      let scrollDiv = parent.querySelector('.generic-select__listbox-wrapper');
      let hasScroll =
        scrollDiv.scrollHeight > scrollDiv.clientHeight && event.relatedTarget;

      if (focused) {
        this.focus = true;
      } else {
        if (this.isMultiSelectClick(event)) {
          this.focus = true;
          event.target.focus();
        }
        // START select with scrollbar
        else if (hasScroll) {
          this.focus = true;
          let focusEvent = event;
          this.targetClickArea(focusEvent);
        }
        // END select with scrollbar
        else {
          this.focus = false;
          this.$emit('handleValidation');
          setTimeout(() => {
            this.handleExpand(event, false);
          }, 250);
        }
      }
    },
    targetClickArea(focusEvent) {
      eventBus.$on('form:clicked', () => {
        this.focus = false;
        this.$emit('handleValidation');
        setTimeout(() => {
          this.handleExpand(focusEvent, false);
        }, 250);
      });
    },
    isMultiSelectClick(event) {
      const listboxId = event.target.parentElement
        .querySelector('.generic-select__listbox')
        .getAttribute('id');
      const clickTargetId = event.relatedTarget?.getAttribute('id');
      return this.multiSelect && clickTargetId === listboxId;
    },
    focusSelectedOption(event, target) {
      const { focusedOption, firstOption, activeOption } =
        this.getOptions(event);
      focusedOption?.classList.remove('-focused');
      if (target) {
        target.classList.add('-focused');
        this.setActiveDescendant(target);
      } else if (activeOption) {
        activeOption.classList.add('-focused');
        this.setActiveDescendant(activeOption);
      } else {
        firstOption.classList.add('-focused');
        this.setActiveDescendant(firstOption);
      }
    },
    setActiveDescendant(target) {
      const optionId = target.getAttribute('id');
      this.activeDescendant = optionId;
    },
    navigateList(event) {
      if (!this.expanded) return;
      event.preventDefault();
      const pressedKey = event.key;
      const {
        firstOption,
        lastOption,
        focusedOption,
        listBoxWrapper,
        allChildren,
      } = this.getOptions(event);
      const isFirstOption = focusedOption === allChildren[0];
      const isLastOption =
        focusedOption === allChildren[allChildren.length - 1];
      const nextWrapper = focusedOption?.nextElementSibling;
      const previousWrapper = focusedOption?.previousElementSibling;

      if (!focusedOption) {
        this.focusSelectedOption(event, firstOption);
        listBoxWrapper.scrollTop = 15;
        return;
      }

      switch (pressedKey) {
        case 'ArrowDown':
          if (isLastOption) {
            this.focusSelectedOption(event, firstOption);
            listBoxWrapper.scrollTop = 0;
          } else {
            this.focusSelectedOption(event, nextWrapper);
            listBoxWrapper.scrollTop += focusedOption.offsetHeight;
          }
          break;
        case 'ArrowUp':
          if (isFirstOption) {
            this.focusSelectedOption(event, lastOption);
            listBoxWrapper.scrollTop = lastOption.offsetTop;
          } else {
            this.focusSelectedOption(event, previousWrapper);
            listBoxWrapper.scrollTop -= focusedOption.offsetHeight;
          }
          break;
        default:
          this.focusSelectedOption(event, firstOption);
          listBoxWrapper.scrollTop = 0;
          break;
      }
    },
    collapseFromListbox(event) {
      const focusedOption = event.target.querySelector('.-focused');
      focusedOption?.classList.remove('-focused');
      this.expanded = false;
    },
    handleOnChange(event, isKeyboardEvent) {
      if (
        event instanceof MouseEvent &&
        event.target.classList.contains('generic-select__listbox')
      ) {
        this.collapseFromListbox(event);
      } else {
        this.$emit('handleInput', event, isKeyboardEvent);
      }
    },
    getOptions(event) {
      const targetElement = event.target;
      const regex = /select-(.*)/;
      const id = regex.exec(targetElement.id);
      const listBox = document.getElementById(
        `generic-select__listbox--${id[1]}`
      );
      const listBoxWrapper = listBox.querySelector(
        '.generic-select__listbox-wrapper'
      );
      const allChildren = listBoxWrapper.children;
      const firstOption = allChildren[0];
      const lastOption = allChildren[allChildren.length - 1];
      const activeOption = listBoxWrapper.querySelector('.-selected');
      const focusedOption = listBoxWrapper.querySelector('.-focused');
      return {
        listBox,
        listBoxWrapper,
        allChildren,
        firstOption,
        lastOption,
        activeOption,
        focusedOption,
      };
    },
  },
};
</script>
